import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, inject, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { deepCopy } from '@app/api/core/utils/deep-copy.utils';
import { hasAnyProperty, isNullOrUndefined } from '@app/api/core/utils/util';
import { SelectOrCreateConfig } from '@app/shared/components/form/select-or-create/select-or-create.types';
import { SingleBankverbindungComponent } from '@app/shared/components/form/single-bankverbindung/single-bankverbindung.component';
import { FieldPropertyDescriptor, Options } from '@app/shared/dynamic-form/util/descriptor/descriptor-types';
import { createDescriptorMap } from '@app/shared/dynamic-form/util/descriptor/descriptor-util';
import { FormatBankverbindungPipe } from '@app/shared/pipes/formatbankverbindung.pipe';
import { ReactiveFormService } from '@app/shared/services/form/reactive-form.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Adresse, Bankverbindung, FieldProperty, Inkasso, ObjektBase, Payload, Person } from '@o30/entities';
import { EMPTY, noop, Observable } from 'rxjs';
import { first, skip, take } from 'rxjs/operators';
import { valueOf } from '../../../comparator/comparator';
import { compareInkasso } from '../../../comparator/gen/inkasso-comparator';
import { compareObjektBase } from '../../../comparator/gen/objektbase-comparator';
import { DescriptorMap } from '../../../dynamic-form/descriptor-map.model';
import { DynamicFormComponent } from '../../../dynamic-form/dynamic-form.component';
import { FormatAdressPipe } from '../../../pipes/formatadress.pipe';
import { DataService } from '../../../services/data/data.service';
import { DisplayOption } from '../../../services/ui/message/message';
import { MessageService } from '../../../services/ui/message/message.service';
import { AdresseComponent } from '../../form/address/adresse.component';
import { BasicCheckboxComponent } from '../../form/basic-checkbox/basic-checkbox.component';
import { BasicDropdownComponent } from '../../form/basic-dropdown/basic-dropdown.component';
import { BasicInputComponent } from '../../form/basic-input/basic-input.component';
import { BeitragszahlerComponent } from '../../form/beitragszahler/beitragszahler.component';
import { SelectOrCreateComponent } from '../../form/select-or-create/select-or-create.component';

@UntilDestroy()
@Component({
  selector:        'app-inkasso',
  templateUrl:     './inkasso.component.html',
  styleUrls:       ['./inkasso.component.scss'],
  providers:       [
    {
      provide:     NG_VALUE_ACCESSOR,
      multi:       true,
      useExisting: forwardRef(() => InkassoComponent)
    }
  ],
  standalone:      true,
  imports:         [
    NgIf,
    MatIcon,
    DynamicFormComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class InkassoComponent implements ControlValueAccessor {
  dataService = inject(DataService);
  messageService = inject(MessageService);
  private reacticeFormService = inject(ReactiveFormService);

  @Input()
  polizzeId: number;
  @Input()
  vkeId: string;
  @Input()
  currentKundeID: number;
  @Input()
  saveDirectly = true;

  @Output()
  beitragszahlweChangeEvent: EventEmitter<ObjektBase> = new EventEmitter();

  @ViewChild(DynamicFormComponent, {static: true})
  private dynamicForm: DynamicFormComponent<Inkasso>;

  // ngModel
  inkasso: Inkasso;
  // dynamicForm descriptor
  description: DescriptorMap<Inkasso>;

  // actual person, Inkasso.beitragszahler only holds IDs
  private person: Person;
  private backup: Inkasso;

  private onTouchedCallback = noop;
  private onChangeCallback: (_: Bankverbindung) => void = noop;

  private ignoreNext: (keyof Inkasso)[] = [];

  savedataToInkassoCallback: Function;

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--lifecycle hooks--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--initialisation--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  init(): void {
    if (!this.inkasso) {
      console.error('inkasso not specified');
      return;
    }
    if (!this.backup) {
      this.backup = deepCopy(this.inkasso);
    }
    this.savedataToInkassoCallback =
      this.saveDirectly ?
        (polizzeId: number, inkasso: Inkasso) => {
          const diff = compareInkasso(this.backup, inkasso) as Inkasso;
          return diff ? this.dataService.putInkasso(polizzeId, diff) : EMPTY;
        }
        : noop;

    const _self = this;
    const labels: { [key in keyof Inkasso]?: string } = {
      bankverbindung: 'Zahlungsart',
      adresse:        'Inkassoadresse',
      zahlweg:        'Zahlweg',
      zahlungsart:    'Zahlweise',
      kundenKonto:    this.kundenKontoLabel
    };
    const options = {
      observables: {
        zahlungsart: this.dataService.getZahlungsarten(this.vkeId)
      }
    } as Options<Inkasso>;
    const descriptor = {
      dsc: {
        beitragszahler: {
          type:          BeitragszahlerComponent,
          context:       {
            styleClass: 'full',
            person:     _self.person,
            editable:   _self.inkasso.editable
          },
          valueAccessor: {
            get: () => _self.inkasso.beitragszahler.value,
            set: newValue => {
              // TODO find out why this emitted twice
              _self.inkasso.beitragszahler.value = newValue;
              this.beitragszahlweChangeEvent.emit(newValue);
              this.refreshPerson();
            }
          }
        },
        adresse:        {
          type:          SelectOrCreateComponent<Adresse>,
          context:       {
            styleClass:          'full',
            labelNew:            'Neue Adresse',
            config:              {
                                   options: this.person ? this.person.adressen : []
                                 } as SelectOrCreateConfig<Adresse>,
            formatPipe:          new FormatAdressPipe(),
            idProperty:          'id',
            createComponent:     AdresseComponent,
            modelGetterCallback: this.dataService.getAdresseModel.bind(this.dataService),
            saveCallback:        this.putPersonAdresse.bind(this),
            enabled:             !!this.personId && this.inkasso.editable,
            allowReset:          false
          },
          valueAccessor: {
            get: () => _self.inkasso.adresse.value,
            set: newValue => {
              _self.inkasso.adresse.value = newValue;
            }
          }
        },
        bankverbindung: {
          type:          SelectOrCreateComponent<Bankverbindung>,
          context:       {
            styleClass:          'full',
            labelNew:            'Neue Bankverbindung',
            config:              {
                                   options: this.bankverbindungOptions
                                 } as SelectOrCreateConfig<Bankverbindung>,
            formatPipe:          new FormatBankverbindungPipe(),
            idProperty:          'index',
            createComponent:     SingleBankverbindungComponent,
            modelGetterCallback: this.dataService.getBankverbindungModel.bind(this.dataService),
            saveCallback:        this.putPersonBankverbindung.bind(this),
            createContext:       {kontotraegerName: this.kontotraegerName},
            enabled:             !!this.personId && this.inkasso.editable,
            allowReset:          false
            // resetValue:          {index: 0}
          },
          valueAccessor: {
            get: () => _self.inkasso.bankverbindung.value,
            set: newValue => {
              _self.inkasso.bankverbindung.value = newValue;
            }
          }
        },
        ...(!this.reacticeFormService.isLeben && {
          zahlungsart: {
            type:    BasicDropdownComponent,
            context: {
              styleClass: 'custom-inkasso'
            }
          }
        }),
        kundenKonto: {
          type:          BasicCheckboxComponent,
          valueAccessor: {
            get: () => {
              return _self.inkasso.kundenKonto.value;
            },
            set: newValue => {
              _self.inkasso.kundenKonto.value = newValue;
            }
          },
          context:       {
            styleClass:   'full inline-50',
            saveDirectly: true,
            saveCallback: _self.savedataToInkassoCallback,
            saveArgs:     [
              _self.polizzeId,
              _self.inkasso
            ]
          }
        }
      }
    } as FieldPropertyDescriptor<Inkasso>;

    if (this.inkasso.externerSchluesselAnzeigen) {
      descriptor.dsc.externerSchluessel = {
        type:          BasicInputComponent,
        valueAccessor: {
          get: () => {
            return _self.inkasso.externerSchluessel.value;
          },
          set: newValue => {
            _self.inkasso.externerSchluessel.value = newValue;
          }
        },
        context:       {
          styleClass:    'full inline-50',
          fieldProperty: _self.inkasso.externerSchluessel,
          saveDirectly:  true,
          saveCallback:  _self.savedataToInkassoCallback,
          showKeys:      false,
          saveArgs:      [
            _self.polizzeId,
            _self.inkasso
          ]
        }
      };
      labels.externerSchluessel = 'Externer Schlüssel';
    }
    this.description = createDescriptorMap(descriptor, this.inkasso, labels, options);

    this.dynamicForm.valueChange$.pipe(
        skip(1),
        untilDestroyed(this))
      .subscribe(newValue => {
        if (this.inkasso.bankverbindung && !this.inkasso.bankverbindung.value) {
          this.resetBankVerbindung();
        }
        if (newValue.key === 'beitragszahler' &&
          !compareObjektBase(valueOf(
              this.backup.beitragszahler, 'value'),
            newValue.value)) {
          return;
        }
        if (this.ignoreNext.findIndex(key => key === newValue.key) !== -1) {
          this.ignoreNext = [];
          return;
        }
        if (this.isIgnoreValueChangeKey(newValue.key)) {
          return;
        }
        if (this.saveDirectly) {
          const diff = compareInkasso(this.backup, this.applyNewValue(this.inkasso, newValue)) as Inkasso;
          if (diff) {
            const beitragszahlerchanged = !!diff.beitragszahler;
            this.dataService.putInkasso(this.polizzeId, diff)
              .pipe(first(), untilDestroyed(this))
              .subscribe(_payload => {
                  if (_payload?.data) {
                    this.backup = deepCopy(_payload.data);
                    if (beitragszahlerchanged) {
                      this.inkasso = _payload.data; // Inkassoadresse und Bankverbindungen müssen sich bei Beitragszahlerwechsel mitändern
                    }
                    if (hasAnyProperty(_payload.data.adresse)) {
                      this.ignoreNext.push('adresse');
                      this.dynamicForm.setValue('adresse', _payload.data.adresse.value);
                    }
                    if (hasAnyProperty(_payload.data.bankverbindung)) {
                      this.ignoreNext.push('bankverbindung');
                      this.dynamicForm.setValue('bankverbindung', this.replaceEmptyOption(_payload.data.bankverbindung.value));
                    }
                  }
                  this.messageService.handlePayload(_payload, {displayOption: DisplayOption.SNACKBAR}, 'Änderung gespeichert');
                }
              );
          }
        }
      });
  }

  private isIgnoreValueChangeKey(key: keyof Inkasso): boolean {
    return (key === 'externerSchluessel');
  }

  /**
   * The valueChanges event is fired after the new value is updated to the FormControl value, and before the change is bubbled up to its parent and ancestors.
   * so the FormControl, has a new value, the valueChange observable emits a value, but the form value itself might not be updated yet
   * let's avoid this approach in the future and save directly like in merkmale
   */
  private applyNewValue(inkasso: Inkasso, newValue: { key: keyof Inkasso, value: any }): Inkasso {
    const result = deepCopy(inkasso);
    if (newValue.key === 'zahlungsart') {
      const value = deepCopy(result.zahlungsart.value);
      value.key = newValue.value;
      result.zahlungsart.value = value;
    } else {
      result[newValue.key].value = newValue.value;
    }
    return result;
  }

  private resetBankVerbindung(): void {
    this.inkasso.bankverbindung.value = this.zahlscheinOption;
  }

  private putPersonAdresse(adresse: Adresse): Observable<Payload<number>> {
    return this.dataService.putPersonAdresse(this.personId, adresse);
  }

  private putPersonBankverbindung(bankverbindung: Bankverbindung): Observable<Payload<number>> {
    return this.dataService.putPersonBankverbindung(this.personId, bankverbindung);
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--ControlValueAccessor implementation--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  writeValue(value: Inkasso) {
    this.inkasso = value;
    if (value) {
      this.init();
    }
  }

  registerOnChange(fn: (_: Bankverbindung) => void) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouchedCallback = fn;
  }

  get personId(): number {
    return this.inkasso?.beitragszahler?.value?.id || null;
  }

  // get the actual Person for inkasso.beitragszahler (ObjektBase)
  private refreshPerson(): void {
    const _self = this;

    if (_self.personId) {
      this.dataService.getPerson(_self.personId)
        .pipe(take(1), untilDestroyed(this))
        .subscribe(_payload => {
          if (_self.messageService.handlePayloadReturnSuccess(_payload)) {
            _self.person = _payload.data;
            _self.dynamicForm.updateContext('beitragszahler', {person: _self.person});
            _self.dynamicForm.updateContext('adresse', {
              config:  {
                options: _self.person.adressen ?
                           _self.person.adressen.filter(_a => _a?.gueltig?.value) : []
              },
              enabled: !!this.personId && this.inkasso.editable
            });
            _self.dynamicForm.updateContext('bankverbindung',
              {
                config:        {options: this.bankverbindungOptions},
                createContext: {kontotraegerName: this.kontotraegerName},
                enabled:       !!this.personId && this.inkasso.editable
              });
            _self.dynamicForm.updateContext('kundenKonto', {
              readonly: this.inkasso.kundenKonto.enabled === false,
              label:    this.kundenKontoLabel
            });
            _self.dynamicForm.markForCheck();
          } else {
            _self.person = null;
          }
        });
    } else {
      _self.person = null;
    }
  }

  get kontotraegerName(): string {
    let name = '';
    if (this.person) {
      if (this.person?.vorname?.value) {
        name += this.person.vorname.value;
      }
      if (this.person?.name?.value) {
        name += ' ' + this.person.name.value;
      }
    }
    return name.trim();
  }

  get kundenKontoLabel(): string {
    let label = 'Abrechnen über Kundenkonto';
    if (this.inkasso?.kundenKontoName) {
      label += ` ${this.inkasso.kundenKontoName}`;
    }
    return label;
  }

  get bankverbindungOptions() {
    const options = this.person ? this.person.bankverbindungen.value : [];
    options.unshift(this.zahlscheinOption);
    return options;
  }

  get zahlscheinOption() {
    return {
      index:   0,
      bic:     null,
      iban:    null,
      name:    {value: 'Zahlschein'} as FieldProperty<string>,
      gueltig: true,
      locked:  false
    };
  }

  private replaceEmptyOption(option) {
    return (!option || isNullOrUndefined(option.index)) ? this.zahlscheinOption : option;
  }
}
